import Bugsnag from "@bugsnag/js";
import "firebase/compat/storage";
import { all, call, fork, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import dbRef from "../../firebase-api/dbRef";
import { loginApi } from "../../firebase-api/login";
import { getItem, setItem } from "../../libs/encryptedStorage";
import { clearHandoffSession, setHandoffSession } from "../../libs/handoffSession";
import fbRef from "../../libs/fbRef";
import authHelper from "../../okta/page";
import Helper from "../../util/helper";
import { callApi } from "../../util/superAgentUtil";
import { clearGodModeSession, getGodModeSession, isSupportTeacher } from "../../util/godMode";
import { teardownClientSession } from "../godMode/saga";
import actions from "../actionType";
import { notification } from "../../util/notification";
import formatMsg from "../../util/formatMsg";
import { timeutil, rolesActions } from "sharedresources";

export function* signIn(request) {
  let payload = request.payload;
  try {

    let password = payload.password;
    let user = payload.user;
    let email = user.email || user.name;
    if (!email) {
      return
    }
    yield call(loginApi.initMasterConfig);
    // No Firebase sign-in for an Auth0 session: the API takes the Auth0 token itself, and
    // the school database is signed in to per school later (GET /settings/dbToken).
    yield call(fetchDevMode);

    let encodedEmail = Helper.getEncodedValue(email);
    setItem("loggedInEncodedEmail", encodedEmail);

    //*****************************************
    yield put({ type: actions.AUTH_SUCCESS, payload: { authSuccess: true } });

    //*****************************************

  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failure in login flow", err);
    yield fork(logout);
  }
}

// Developer-only tools (the student Excel upload in settings) show when the backend says this
// Auth0 user has the devMode flag. A failure only hides those tools.
function* fetchDevMode() {
  try {
    const response = yield call(callApi, undefined, "get", "bookingoApi/signup/devMode", {});
    if (response?.body?.data?.devMode) setItem("devMode", true);
  } catch (err) {
    console.log("Failed to fetch developer mode", err);
  }
}

// Entry point for parents arriving from Illumine. The assertion in the landing URL is
// redeemed for a custom token minted on Bookingo's own Firebase project; from there the
// session is built through the same helpers a normal account login uses, so the storage
// keys, the redux shape and the secondary DB sign-in all stay on one path.
export function* handoffLogin(request) {
  // Sign-in to this project happens partway through, and every step after it can still
  // fail. Tracked so the catch can undo that sign-in — and only that one: a failure before
  // it leaves whatever session was already there, which is not this flow's to end.
  let signedIn = false;
  try {
    const { assertion } = request.payload;
    yield call(loginApi.initMasterConfig);

    // The route authenticates itself with the assertion in the body, so it takes no
    // bearer and no centerId — hence the undefined firebase and unauthorized flag.
    let endpoint = `bookingoApi/handoff/redeem`;
    let response = yield call(callApi, undefined, "post", endpoint, { assertion }, true);
    let handoff = response?.body?.data;
    if (!handoff?.token) {
      throw new Error("Handoff redeem did not return a token");
    }

    let authUser = yield call(loginApi.signInWithCustomToken, handoff.token);
    if (!authUser) {
      throw new Error("Failed to sign in with the handoff token");
    }
    signedIn = true;
    // From here on every API call authenticates with this project's ID token.
    setHandoffSession(handoff.centerId);
    setItem("centerId", handoff.centerId);

    // The redeem response deliberately carries only the identifiers, so the school config is
    // fetched the same way an already-signed-in session refreshes it.
    let schoolConfigResponse = yield call(callApi, { centerId: handoff.centerId }, "get", `bookingoApi/settings/schoolConfig`, {});
    let schoolConfig = schoolConfigResponse?.body?.data?.schoolConfig;
    if (!schoolConfig) {
      throw new Error("School configuration not found for the handoff center");
    }
    if (!schoolConfig.centerId) schoolConfig.centerId = handoff.centerId;

    let account = yield call(fetchAccount, { centerId: handoff.centerId }, handoff.accountId);
    if (!account) {
      throw new Error("Account not found for the handoff session");
    }

    // Same shape GET /signup/:signInProvider hands the user sheet, so continueWithNewUser
    // and everything that reads the stored "user" sees exactly what a normal login writes.
    let newUser = {
      ...account,
      id: handoff.accountId,
      centerId: handoff.centerId,
      signInProvider: account.email || ((account.countryCode || "") + (account.phoneNumber || "")),
      userType: "ACCOUNT"
    };
    if (account.email) {
      setItem("loggedInEncodedEmail", Helper.getEncodedValue(account.email));
    }

    yield call(continueWithNewUser, { payload: { newUser, schoolConfig } });
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failed to redeem handoff", err);
    clearHandoffSession();
    if (signedIn) {
      // Reporting HANDOFF_FAILED while the Firebase session stays open would leave the
      // browser holding a valid ID token for a session the app never finished building.
      try {
        yield call(loginApi.signOutMasterAuth);
      } catch (signOutErr) {
        // Nothing further to do — surfacing this instead of the original failure would
        // only hide why the handoff actually failed.
        console.log("Failed to sign out after a failed handoff", signOutErr);
      }
    }
    yield put({ type: actions.LOGIN_SAGA_SUCCESS, payload: { operationType: "HANDOFF_FAILED", isLoading: false } });
  }
}

// Bookingo's half of the Illumine connect consent. The assertion proves an Illumine admin
// asked for the link; the bearer on this call proves the person who clicked confirm
// administers the Bookingo school being handed over. The backend spends the assertion
// before it does any work, so a failure here is terminal — the admin has to start the
// flow again from Illumine rather than retry.
function* connectIllumineSchool(request) {
  try {
    const { assertion, centerId, teacherId } = request.payload;

    // centerId is appended by hand instead of through a firebase object: getSuperagentBody
    // would add platform/updatedOn to the body, and /illumineConnect/link rejects any key
    // its schema did not ask for.
    let endpoint = `bookingoApi/illumineConnect/link?centerId=${encodeURIComponent(centerId)}`;
    let response = yield call(callApi, undefined, "post", endpoint, { assertion, teacherId });

    yield put({
      type: actions.LOGIN_SAGA_SUCCESS,
      payload: {
        illumineConnection: response?.body?.data,
        operationType: "ILLUMINE_CONNECT_SUCCESS",
        isLoading: false
      }
    });
  } catch (err) {
    // Deliberately not routed through notifyBugsnag: the assertion is a bearer credential
    // and it rides in the request body hanging off this error, so only the status and the
    // server's own message are allowed out.
    console.log("Failed to connect the Illumine school", err?.status);
    yield put({
      type: actions.LOGIN_SAGA_SUCCESS,
      payload: {
        illumineConnectError: err?.response?.body?.data?.message || err?.response?.body?.message || null,
        operationType: "ILLUMINE_CONNECT_FAILED",
        isLoading: false
      }
    });
  }
}

// Through the API, never the school database: the apps may read only its /lastUpdate entries.
function* fetchAccount(firebase, accountId) {
  let response = yield call(callApi, firebase, "get", `bookingoApi/account/${accountId}`, {});
  return response?.body?.data || null;
}

// What the block/deactivate flows leave on an account that must not stay signed in.
function isAccountSignedOut(account) {
  return !!account && !!(account.webSignout || account.blockedByContactId || account.status === "INACTIVE");
}

/**
 * Sign-in to the school's own Firebase project with a custom token for this profile.
 *
 * Best effort: without it the school database's /lastUpdate entries are unreadable, so
 * screens still load through the API but stop refreshing live — no reason to end the login.
 */
function* signInToSchoolDb(secondaryApp, newUser) {
  try {
    const userType = newUser.userType === "ACCOUNT" ? "ACCOUNT" : "TEACHER";
    const endpoint = `bookingoApi/settings/dbToken?userId=${encodeURIComponent(newUser.id)}&userType=${userType}`;
    const response = yield call(callApi, { centerId: newUser.centerId }, "get", endpoint, {}, false, false, true);
    const token = response?.body?.data?.token;
    if (!token) throw new Error("No school database token in the response");
    yield call(loginApi.loginSecondaryDb, secondaryApp, token);
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failed to sign in to the school database", err);
  }
}

function* fetchNewUsers(reqObj: any) {
  try {
    const { email, loginType } = reqObj.payload;
    let endpoint = `bookingoApi/signup/${email}?loginType=${loginType}`
    let response = yield call(callApi, undefined, "get", endpoint, {}, false, false, true);

    if (response.status == 200) {
      if (response.body.data && response.body.data.length) {
        yield put({ type: actions.NEW_USER_SUCCESS, payload: { newUsers: response.body.data, operationType: "NEW_USER_SUCCESS" } });
      } else {
        yield put({ type: actions.NEW_USER_SUCCESS, payload: { newUsers: response.body.data, operationType: "NO_NEW_USER" } });

      }
    } else {
      yield fork(showErrorMsg, "Failed to login. User not found.");
      return;
    }
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failure in login flow", err);
    yield fork(logout);
  }

}

function* continueFromSecondaryDb(schoolConfig: any, newUser: any, encodedEmail: any, noChannel: any, updateLastLogin: boolean) {
  try {
    // /signup/:signInProvider only offers TEACHER and ACCOUNT profiles, so a stored PARENT
    // session can only be a leftover from before parents logged in as accounts.
    if (newUser.userType === "PARENT") {
      yield fork(showErrorMsg, "This login is no longer supported. Please sign in again.");
      return;
    }
    let secondaryApp = yield call(loginApi.initSecondaryDb, schoolConfig, newUser.centerId);
    yield call(signInToSchoolDb, secondaryApp, newUser);
    if (newUser.userType === "ACCOUNT") {
      yield fork(accountLogin, secondaryApp, schoolConfig, newUser, encodedEmail, noChannel)
    }
    else {
      yield fork(teacherLogin, secondaryApp, schoolConfig, newUser, encodedEmail, noChannel, updateLastLogin)
    }

    addBasicDetailsToProject(newUser, schoolConfig);

  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failure in login flow", err);
    yield fork(logout);
  }
}

function* updateTeacherLastLogin(firebase) {
  try {
    let endpoint = "bookingoApi/teacher/" + firebase.teacher.id + "/lastLogin";
    yield call(callApi, firebase, "post", endpoint, {});
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log(err)
  }
}

function* getAllStaff(request) {
  let updateTeacherChan = null;
  try {
    let { firebase } = request.payload;

    // Validate firebase.teacher exists before proceeding
    if (!firebase?.teacher?.id) {
      console.error("Firebase teacher not available");
      yield put({ type: actions.STAFF_REQUEST_FAILED });
      return;
    }

    updateTeacherChan = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateTeacher);
    yield put({ type: actions.LOGIN_SAGA_SUCCESS, payload: { firebase: { ...firebase } } });

    // Fetch initial data immediately
    let endpoint = "bookingoApi/teacher?teacherId=" + firebase.teacher.id;
    let initialResponse = yield call(callApi, firebase, "get", endpoint, {}, false, false, true);
    if (initialResponse.status == 200) {
      let staffs = initialResponse.body.data?.teachers.filter((item) => !item.deleted) || [];
      setItem("teacherList", staffs);
      yield put({ type: actions.STAFF_REQ_SUCCESS, payload: { isLoading: false } });
      yield put({ type: actions.SET_TEACHERS_FETCHED, payload: {} });
      yield put({
        type: actions.SET_FIREBASE_OBJ,
        payload: { teacherList: staffs }
      });
    }

    // The channel opens with the current value; the list was just fetched for it.
    yield take(updateTeacherChan);
    while (true) {
      let lastUpdateNotification = yield take(updateTeacherChan);
      let endpoint = "bookingoApi/teacher?teacherId=" + firebase.teacher.id;
      let response = yield call(callApi, firebase, "get", endpoint, {}, false, false, true);
      if (response.status == 200) {
        let staffs = response.body.data?.teachers.filter((item) => !item.deleted) || [];
        setItem("teacherList", staffs);
        yield put({ type: actions.STAFF_REQ_SUCCESS, payload: { isLoading: false } });
        yield put({ type: actions.SET_TEACHERS_FETCHED, payload: {} });
        yield put({
          type: actions.SET_FIREBASE_OBJ,
          payload: { teacherList: staffs }
        });
        yield put({ type: actions.UPDATE_CHANNEL, payload: { key: "teacherChan", val: updateTeacherChan } });
      } else {
        yield put({ type: actions.STAFF_REQUEST_FAILED });
      }
    }
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    yield put({ type: actions.STAFF_REQUEST_FAILED });
    console.log("failed get all staff  ", err);
  } finally {
    if (updateTeacherChan) {
      updateTeacherChan.close();
    }
    console.log("teacher channel closed");
  }
}

function* getAllUsers(request) {
  try {
    let { firebase } = request.payload;
    const updateUserChan = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateUsers);
    while (true) {
      let lastUpdateUser = yield take(updateUserChan);
      let endpoint = "bookingoApi/user/all"
      let response = yield call(callApi, firebase, "get", endpoint, {});
      if (response.status == 200) {
        let users: any = response.body.data?.filter((item) => !item.deleted) || [];
        users = users.map(user => ({ ...user, childConfig: null }))
        setItem("userList", users)
        yield put({ type: actions.USERS_SAGA_SUCCESS, payload: { userList: users, isLoading: false } });
        yield put({ type: actions.UPDATE_CHANNEL, payload: { key: "userChan", val: updateUserChan } });
      }
      else {
        console.log("[getAllUsers] failed get all users");
      }
    }
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("[getAllUsers] failed get all users  ", err);
  } finally {
    console.log("[getAllUsers] user channel closed")
  }
}

function* getAllAccounts(request) {
  try {
    let { firebase } = request.payload;
    const updateAccountChan = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateAccounts);
    while (true) {
      let lastUpdateAccount = yield take(updateAccountChan);
      let endpoint = "bookingoApi/account/all"
      let response = yield call(callApi, firebase, "get", endpoint, {});

      if (response.status == 200) {
        let accounts = response.body.data?.filter((item) => !item.deleted) || [];
        setItem("accountList", accounts)
        yield put({ type: actions.ACCOUNTS_SAGA_SUCCESS, payload: { accountList: accounts, isLoading: false } });
        yield put({ type: actions.UPDATE_CHANNEL, payload: { key: "accountChan", val: updateAccountChan } });
      }
      else {
        console.log('[getAllAccounts] failed to get all accounts');
      }
    }
  }
  catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log('[getAllAccounts] failed to get all accounts', err);
  }
  finally {
    console.log("[getAllAccounts] accounts channel closed");
  }
}

function* teacherLogin(secondaryApp, schoolConfig: { branchPath: any; }, newUser: any, encodedEmail: any, noChannel: any, updateLastLogin: boolean) {
  let centerIds = []
  if (newUser.centers)
    centerIds = newUser.centers.map(center => center.id);
  newUser.centerIds = centerIds;
  setItem("teacher", newUser);
  // let teacher = yield call(loginApi.fetchTeacher, secondaryApp, schoolConfig.branchPath, newUser.teacherId);

  // setItem("teacher", teacher);
  //*****************************************

  // let studentList = yield call(loginApi.fetchStudents, secondaryApp, schoolConfig.branchPath);
  // setItem("studentList", studentList);

  //*****************************************

  let firebaseObj = createFirebaseObj(secondaryApp, schoolConfig, newUser, { teacher: newUser });

  // God Mode: the support identity has no /teachers record by design, so there is
  // nothing to fetch or merge. It already carries permissionMap { all: true }
  // from the server, and read-only enforcement lives on the API, not here.
  if (isSupportTeacher(newUser)) {
    yield put({ type: actions.SIGN_SUCCESS, payload: firebaseObj });
    // Before the all() below, which never returns: the staff, user and account loaders
    // keep listening to their channels for the whole session.
    if (!noChannel) {
      yield fork(enableSchoolConfigChannel, firebaseObj);
    }
    yield all([
      call(getAllAccounts, { payload: { firebase: firebaseObj } }),
      call(getAllUsers, { payload: { firebase: firebaseObj } }),
      call(getAllStaff, { payload: { firebase: firebaseObj } }),
      call(getSchoolConfig, { payload: { firebase: firebaseObj } })
    ]);
    return;
  }

  // Fetch teacher data from backend
  try {
    const teacherResponse = yield call(callApi, firebaseObj, "get", `bookingoApi/teacher/${newUser.id}`, {});

    if (teacherResponse.status === 200) {
      const teacherData = teacherResponse.body.data;
      // Merge teacher data with newUser
      Object.assign(newUser, teacherData);
    }

    // Check if teacher is super admin
    if (newUser.isSuperAdmin) {
      // Super admin has all permissions, no need to fetch permissions
      newUser.permissionMap = { all: true };
    } else {
      // Fetch permissions for non-super admin users
      try {
        const permissionResponse = yield call(callApi, firebaseObj, "get", `bookingoApi/teacher/${newUser.id}/permissions`, {});
        if (permissionResponse.status === 200) {
          const permissionData = permissionResponse.body.data;
          newUser.permissionMap = permissionData.permissions || {};
        }
      } catch (permissionErr) {
        console.log("Failed to fetch permission map:", permissionErr);
      }
    }

    // Update firebase object with updated teacher data
    firebaseObj.teacher = newUser;
    console.log("firebaseObj.teacher", firebaseObj.teacher)
  } catch (err) {
    console.log("Failed to fetch teacher data:", err);
  }

  //*****************************************



  yield put({ type: actions.SIGN_SUCCESS, payload: firebaseObj });
  //*****************************************

  if (updateLastLogin) {
    yield updateTeacherLastLogin(firebaseObj);
  }

  // Before the all() below, which never returns: the staff, user and account loaders keep
  // listening to their channels for the whole session, so anything after it never ran and
  // the school config (logo, theme, timezone, ...) never refreshed live.
  if (!noChannel) {
    yield fork(enableSchoolConfigChannel, firebaseObj);
  }

  yield all([
    call(getAllAccounts, { payload: { firebase: firebaseObj } }),
    call(getAllUsers, { payload: { firebase: firebaseObj } }),
    call(getAllStaff, { payload: { firebase: firebaseObj } }),
    call(getSchoolConfig, { payload: { firebase: firebaseObj } })
  ]);
}

function* accountLogin(secondaryApp, schoolConfig: { branchPath: any; }, newUser: any, encodedEmail: any, noChannel: any) {
  let account = yield call(fetchAccount, { centerId: newUser.centerId }, newUser.id);
  if (isAccountSignedOut(account)) {
    yield call(logout);
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
    return;
  }

  let firebaseObj = createFirebaseObj(secondaryApp, schoolConfig, newUser, { account });
  yield put({ type: actions.SIGN_SUCCESS, payload: firebaseObj });
  yield put({ type: actions.LOGIN_SAGA_SUCCESS, payload: { newUsers: [] } });

  // Watch the account node for blocks — ALWAYS, regardless of noChannel. A fresh
  // login goes through continueWithNewUser (noChannel=true) and the dashboard's
  // reinstate effect doesn't re-run on client-side navigation, so gating this on
  // noChannel would leave the just-logged-in session with no block watch until a
  // full page reload.
  yield fork(enableAccountBlockChannel, firebaseObj, newUser.id);
  yield call(getSchoolConfig, { payload: { firebase: firebaseObj } });
}

function addBasicDetailsToProject(newUser, schoolConfig) {
  try {
    Bugsnag.setUser(
      newUser.email,
      newUser.email,
      newUser.centerId,
    );
    timeutil.setDefaultTimezone(schoolConfig.timezone);
  }
  catch (error) {
    Helper.notifyBugsnag(error, true);
    console.log("error to setup basic detail of user ", error);
  }

}

function createFirebaseObj(secondaryApp, schoolConfig, newUser, loggedInEntities) {
  return {
    secondaryApp: secondaryApp,
    secondaryDb: secondaryApp.database(),
    secondaryStorage: secondaryApp.storage(),
    sbp: schoolConfig.branchPath,
    sbDbName: schoolConfig.centerId,
    user: newUser,
    schoolConfig: schoolConfig,
    ...loggedInEntities
  }
}

function* showErrorMsg(msg) {
  alert(msg);
  yield fork(logout);
  console.log("error", msg);
}

// Users portal (account) watch: blocking or deactivating an account bumps its
// lastUpdate/accountStatus entry; on each bump the account is re-read and, when it
// may no longer be signed in, the session ends and goes to the home page.
function* enableAccountBlockChannel(firebase, accountId) {
  const chan = yield call(dbRef.getLastUpdateChannel, firebase, `${fbRef.lastUpdateAccountStatus}/${accountId}`);
  yield put({ type: actions.UPDATE_CHANNEL, payload: { key: "accountBlock", val: chan } });
  try {
    // The channel opens with the current value; accountLogin has just checked the account.
    yield take(chan);
    while (true) {
      yield take(chan);
      let account = null;
      try {
        account = yield call(fetchAccount, firebase, accountId);
      } catch (err) {
        console.log("Failed to re-check the account status", err);
        continue;
      }
      if (isAccountSignedOut(account)) {
        yield call(logout);
        if (typeof window !== "undefined") {
          window.location.href = "/";
        }
        return;
      }
    }
  } finally {
    chan.close();
    console.log("end account block channel");
  }
}

// The school's /schools row is not readable by the apps; a change to it bumps
// lastUpdate/schoolConfig in the school database, answered by re-fetching it.
function* enableSchoolConfigChannel(firebase) {
  const chan = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateSchoolConfig);
  yield put({ type: actions.UPDATE_CHANNEL, payload: { key: "schoolConfig", val: chan } });
  try {
    // The channel opens with the current value; the login has just fetched the config.
    yield take(chan);
    while (true) {
      yield take(chan);
      try {
        yield call(getSchoolConfig, { payload: { firebase } });
      } catch (err) {
        console.log("Failed to refresh the school config", err);
      }
    }
  } finally {
    chan.close();
    console.log("end school config channel");
  }
}

export function* logout() {

  try {
    // A full sign-out ends any open God Mode session too, so the audit trail shows
    // a real end rather than a session that simply stopped being used. Best effort:
    // the session expires on its own, and logging out must never be blocked by it.
    // (Leaving a client without leaving Bookingo is the switch flow instead —
    // switchGodModeClient in redux/godMode/saga.ts.)
    // Deliberately no API call here. logout() also runs on paths where the token
    // has already failed, and authHelper.getAuthToken responds to a failure by
    // calling loginApi.logout() itself — so an authenticated request on this path
    // can cascade. The session is closed properly by the switch flow, and the
    // server expires it regardless.
    // Storage drops a session once it expires locally; the store still has the
    // centerId needed to sign out of the school's database.
    const godModeSession = getGodModeSession() || (yield select((state: any) => state.godMode?.session));
    if (godModeSession?.centerId) {
      yield call(loginApi.signOutSecondaryDb, godModeSession.centerId);
    }
    // Drop the God Mode pointer: whatever happens to the Auth0/Firebase sign-out
    // below, the next session must not inherit this one.
    clearGodModeSession();
    yield call(loginApi.initMasterConfig);
    yield call(loginApi.logout);
    yield put({ type: actions.CLOSE_CHANNELS })
    yield put({ type: actions.SIGN_OUT_SUCCESS, payload: null })
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failed to logout", err);
  }

}

function* reinstateLogin(request) {
  let payload = request.payload;
  try {
    yield call(loginApi.initMasterConfig);

    let newUser = JSON.parse(getItem("user"));
    if (!newUser) {
      yield fork(showErrorMsg, "Failed to login. New user not found.");
      return;
    }
    //*****************************************

    let schoolConfig = JSON.parse(getItem("schoolConfig"));
    if (!schoolConfig) {
      yield fork(showErrorMsg, "Failed to login. School configuration not found.");
      return;
    }
    //*****************************************

    let encodedEmail = getItem("loggedInEncodedEmail");
    yield fork(continueFromSecondaryDb, schoolConfig, newUser, encodedEmail, payload?.noChannel, false);

  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("Failed to reinstate login", err);
    yield fork(logout);
  }
}

function* signUpAccount(request) {
  try {
    let payload = request.payload;
    // let response = yield call(callApi, firebase, "post", endpoint, reqObj);
    var response = yield call(
      loginApi.signupAccount,
      payload.email,
      payload.password,
      payload.countryCode,
      payload.phoneNumber,
      payload.name,
      payload.address,
      payload.companyName,
      payload.currency,
      payload.industry,
      payload.confirmPassword,
      payload.firebase,
      payload.gender,
      payload.timezone
    );


    if (response && response.status == 200) {
      notification("success", response.body.message);
      yield put({
        type: actions.SIGN_UP_SUCCESS,
        signupStatus: response,

      });
    } else {
      notification("error", response.body.message);
      yield put({
        type: actions.LOGIN_SAGA_FAILED,
      });
    }
  } catch (err) {
    Helper.notifyBugsnag(err, true);
    console.log("failed to sign up account", err);
    yield put({
      type: actions.LOGIN_SAGA_FAILED,
    });
  }
}
function* initLogin(request) {
  try {
    //
    console.log("it is working", request.payload.loginType)
    let auth0 = yield call(authHelper.getauth0Instance, request.payload.loginType)
  } catch (err) {
    console.log("failed to sign up account", err);
  }
}

function* continueWithNewUser(request) {
  const { newUser, schoolConfig } = request.payload;
  let newUserObj
  newUser.centerId = schoolConfig.centerId;
  if (newUser.userType === "PARENT" || newUser.userType === "ACCOUNT") {
    newUser.schoolName = newUser.centerId;
  }

  // An operator entering one of their own companies from the picker may still
  // have a God Mode session open. Close it before signing in, or its header,
  // banner and school Firebase session carry over into their own account.
  if (!isSupportTeacher(newUser)) {
    const godModeSession = getGodModeSession() || (yield select((state: any) => state.godMode?.session));
    if (godModeSession) {
      yield call(teardownClientSession, godModeSession, "Operator entered their own account", false);
    }
  }

  let encodedEmail = Helper.getEncodedValue(newUser.signInProvider);
  setItem("user", newUser);
  setItem("schoolConfig", schoolConfig);
  if (!schoolConfig) {
    yield fork(showErrorMsg, "Failed to login. School configuration not found.");
    return;
  }
  // updateLastLogin is off for God Mode: writing lastLogin would create a
  // /teachers node for the support identity and put the operator in the client's
  // staff list, which is exactly what God Mode must never do.
  yield fork(continueFromSecondaryDb, schoolConfig, newUser, encodedEmail, true, !isSupportTeacher(newUser));
}

function* setFirebaseObj(request) {
  const { schoolConfig } = request.payload;
  if (schoolConfig) {
    timeutil.setDefaultTimezone(schoolConfig.timezone);
    schoolConfig && setItem("schoolConfig", schoolConfig);
  }
}

function* getSchoolConfig(request) {
  const { firebase } = request.payload;

  let endpoint = `bookingoApi/settings/schoolConfig`
  let response = yield call(callApi, firebase, "get", endpoint, {});
  if (response.body.data?.schoolConfig) {
    let schoolConfig = response.body.data?.schoolConfig;
    if (schoolConfig.deactivate) {
      yield fork(showErrorMsg, "School is deactivated.");
      return;
    }
    yield put({
      type: actions.SET_FIREBASE_OBJ,
      payload: {
        schoolConfig: schoolConfig
      }
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.SIGN_IN, signIn);
  yield takeLatest(actions.SIGN_OUT, logout);
  yield takeLatest(actions.VALIDATE_USER_LOGIN, reinstateLogin);
  yield takeLatest(actions.SIGN_UP_ACCOUNT, signUpAccount)
  yield takeLatest(actions.CONTINUE_WITH_NEW_USER, continueWithNewUser);
  yield takeLatest(actions.HANDOFF_LOGIN, handoffLogin);
  yield takeLatest(actions.CONNECT_ILLUMINE_SCHOOL, connectIllumineSchool);
  yield takeLatest(actions.FETCH_NEW_USERS, fetchNewUsers);
  yield takeLatest(actions.INIT_LOGIN, initLogin)
  yield takeLatest(actions.SET_FIREBASE_OBJ, setFirebaseObj)
  yield takeLatest(actions.GET_SCHOOL_CONFIG, getSchoolConfig)
}

